import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Avatar,
  InputAdornment,
  Paper,
  Collapse,
  IconButton,
  Pagination,
  Button,
  Switch,
} from '@mui/material';
import {
  Description,
  CalendarToday,
  ExpandMore,
  ChevronRight,
  AccountBalance,
  CloudUpload,
  UnfoldMore,
  UnfoldLess,
  Search,
  Close,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../contexts/CompanyContext';
import { toast } from 'react-toastify';
import { EDEFTER_API_URL } from '../../config/edefterApi';
import AuthService from '../../services/authService';

const GeneralJournal = () => {
  const navigate = useNavigate();
  const { taxId } = useCompany();
  const selectedIdentifier = taxId;
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [entryHeaders, setEntryHeaders] = useState([]); // Sadece header'lar
  const [entryDetailsCache, setEntryDetailsCache] = useState(new Map()); // EntryHeader ID -> details
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // Hangi header'ların detayları yükleniyor
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(50); // Sayfa başına kayıt sayısı
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [accountCodeSearch, setAccountCodeSearch] = useState('');
  const [debouncedAccountCodeSearch, setDebouncedAccountCodeSearch] = useState('');
  const [shouldExpandAll, setShouldExpandAll] = useState(false);

  // TaxId değiştiğinde state'leri temizle
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setError(null);
      return;
    }
  }, [selectedIdentifier]);

  // Fetch documents when identifier is selected
  useEffect(() => {
    if (!selectedIdentifier) {
      setDocuments([]);
      setSelectedDocument(null);
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      return;
    }

    const fetchDocuments = async () => {
      try {
        setLoadingDocuments(true);
        const response = await AuthService.authenticatedFetch(`${EDEFTER_API_URL}/documents-by-identifier?identifier=${selectedIdentifier}`);
        if (response.ok) {
          const data = await response.json();
          // Sort documents: by year (descending), then by month (1-12)
          const sortedDocuments = (data || []).sort((a, b) => {
            const dateA = new Date(a.periodCoveredStart);
            const dateB = new Date(b.periodCoveredStart);
            
            // Get year and month from period start date
            const yearA = dateA.getFullYear();
            const monthA = dateA.getMonth() + 1; // getMonth() returns 0-11, so add 1
            const yearB = dateB.getFullYear();
            const monthB = dateB.getMonth() + 1;
            
            // First sort by year (descending - newer years first)
            if (yearB !== yearA) {
              return yearB - yearA;
            }
            
            // Then sort by month (ascending - 1 to 12)
            return monthA - monthB;
          });
          
          setDocuments(sortedDocuments);
        } else {
          setError('Failed to load documents');
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    fetchDocuments();
  }, [selectedIdentifier]);

  // Track previous search value to detect actual changes
  const prevAccountCodeSearchRef = React.useRef('');

  // Debounce account code search (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update debounced value if it actually changed
      if (prevAccountCodeSearchRef.current !== accountCodeSearch) {
        // Reset page and clear cache when search term changes
        if (prevAccountCodeSearchRef.current !== '' || accountCodeSearch !== '') {
          setPage(1);
          setEntryDetailsCache(new Map());
        }
        
        prevAccountCodeSearchRef.current = accountCodeSearch;
        setDebouncedAccountCodeSearch(accountCodeSearch);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [accountCodeSearch]);


  // Fetch entry headers when document is selected, page changes, or search changes
  useEffect(() => {
    if (!selectedDocument) {
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    const fetchEntryHeaders = async () => {
      try {
        setLoading(true);
        setError(null);
        const pageIndex = page - 1; // Backend 0-based, frontend 1-based
        
        // Build URL with optional accountCode parameter
        let url = `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=${pageIndex}&size=${rowsPerPage}`;
        if (debouncedAccountCodeSearch && debouncedAccountCodeSearch.trim()) {
          url += `&accountCode=${encodeURIComponent(debouncedAccountCodeSearch.trim())}`;
        }
        
        const response = await AuthService.authenticatedFetch(url);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.error) {
            console.error('[GeneralJournal] Backend error:', data.error);
            setError(data.error);
            return;
          }
          
          const newHeaders = data.entryHeaders || [];
          setEntryHeaders(newHeaders);
          setTotalPages(data.totalPages || 0);
          setTotalElements(data.totalElements || 0);

          // If shouldExpandAll is true (user manually enabled), expand all entries and load their details
          if (shouldExpandAll && newHeaders.length > 0) {
            const allHeaderIds = new Set(newHeaders.map(h => h.id));
            allHeaderIds.forEach(headerId => {
              if (!entryDetailsCache.has(headerId)) {
                fetchEntryDetails(headerId);
              }
            });
            setExpandedEntries(allHeaderIds);
          } else if (!shouldExpandAll) {
            // Reset expanded entries only if expand all is not active (user manually disabled)
            setExpandedEntries(new Set());
          }
          // Note: Background fetching for search is handled in separate useEffect
    // eslint-disable-next-line react-hooks/exhaustive-deps
        } else {
          const errorText = await response.text();
          console.error('[GeneralJournal] Error response:', errorText);
          setError('Failed to load journal entries: ' + errorText);
        }
      } catch (err) {
        console.error('Error fetching entry headers:', err);
        setError('Failed to load journal entries: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEntryHeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, page, rowsPerPage, debouncedAccountCodeSearch]);

  // Background fetch: Load details for all headers when search is active (silently, without expanding)
  // Use batch request instead of individual requests for better performance
  useEffect(() => {
    const hasSearch = debouncedAccountCodeSearch && debouncedAccountCodeSearch.trim();
    if (hasSearch && entryHeaders.length > 0 && selectedDocument) {
      // Collect all header IDs that need details
      const headerIdsToFetch = entryHeaders
        .map(header => header.id)
        .filter(id => !entryDetailsCache.has(id) && !loadingDetails.has(id));

      if (headerIdsToFetch.length > 0) {
        // Use batch request instead of individual requests
        fetchEntryDetailsBatch(headerIdsToFetch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryHeaders, debouncedAccountCodeSearch, selectedDocument]);

  // Helper function to flatten entry details
  const flattenEntryDetails = useCallback((entryHeaderData) => {
    const flattenedDetails = [];
    if (entryHeaderData?.entryDetails && Array.isArray(entryHeaderData.entryDetails)) {
      entryHeaderData.entryDetails.forEach((detail) => {
        if (detail.accounts && Array.isArray(detail.accounts) && detail.accounts.length > 0) {
          detail.accounts.forEach((account) => {
            flattenedDetails.push({
              entryDetailId: detail.id,
              lineNumber: detail.lineNumber,
              postingDate: detail.postingDate,
              amount: detail.amount,
              debitCreditCode: detail.debitCreditCode,
              documentType: detail.documentType,
              documentNumber: detail.documentNumber,
              documentDate: detail.documentDate,
              detailComment: detail.detailComment,
              accountMainId: account.mainId,
              accountMainDescription: account.mainDescription,
              accountSubId: account.accountSub?.subId || null,
              accountSubDescription: account.accountSub?.subDescription || null,
            });
          });
        } else {
          flattenedDetails.push({
            entryDetailId: detail.id,
            lineNumber: detail.lineNumber,
            postingDate: detail.postingDate,
            amount: detail.amount,
            debitCreditCode: detail.debitCreditCode,
            documentType: detail.documentType,
            documentNumber: detail.documentNumber,
            documentDate: detail.documentDate,
            detailComment: detail.detailComment,
            accountMainId: null,
            accountMainDescription: null,
            accountSubId: null,
            accountSubDescription: null,
          });
        }
      });
    }
    return flattenedDetails;
  }, []);

  // Fetch entry details in batch (for multiple headers at once)
  const fetchEntryDetailsBatch = useCallback(async (entryHeaderIds) => {
    if (!entryHeaderIds || entryHeaderIds.length === 0) return;

    // Filter out already cached or currently loading IDs
    const idsToFetch = entryHeaderIds.filter(id => 
      !entryDetailsCache.has(id) && !loadingDetails.has(id)
    );

    if (idsToFetch.length === 0) return;

    try {
      // Mark all as loading
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        idsToFetch.forEach(id => newSet.add(id));
        return newSet;
      });


      // Create comma-separated string of IDs
      const idsString = idsToFetch.join(',');
      
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entry-details-batch?entryHeaderIds=${idsString}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          console.error('[GeneralJournal] Backend error:', data.error);
          // Fallback to individual requests if batch fails
          idsToFetch.forEach(id => fetchEntryDetails(id));
          return;
        }

        // Process batch response - expect array of entry headers with details
        const batchResults = Array.isArray(data) ? data : (data.results || data.entryHeaders || []);
        
        // Update cache for all fetched headers
        const newCache = new Map(entryDetailsCache);
        batchResults.forEach((entryHeaderData) => {
          if (entryHeaderData?.entryHeaderId) {
            const headerId = entryHeaderData.entryHeaderId;
            const flattenedDetails = flattenEntryDetails(entryHeaderData);
            newCache.set(headerId, flattenedDetails);
          }
        });
        
        setEntryDetailsCache(newCache);
      } else {
        // If batch endpoint doesn't exist, fallback to individual requests
        console.warn('[GeneralJournal] Batch endpoint not available, falling back to individual requests');
        idsToFetch.forEach(id => fetchEntryDetails(id));
      }
    } catch (err) {
      console.error('[GeneralJournal] Error fetching details in batch:', err);
      // Fallback to individual requests on error
      idsToFetch.forEach(id => fetchEntryDetails(id));
    } finally {
      // Remove from loading set
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        idsToFetch.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [entryDetailsCache, loadingDetails, flattenEntryDetails]);

  // Fetch entry details when a row is expanded (single header)
  const fetchEntryDetails = useCallback(async (entryHeaderId) => {
    // Eğer zaten cache'de varsa, tekrar yükleme
    if (entryDetailsCache.has(entryHeaderId)) {
      return;
    }

    // Eğer zaten yükleniyorsa, tekrar başlatma
    if (loadingDetails.has(entryHeaderId)) {
      return;
    }

    try {
      setLoadingDetails(prev => new Set(prev).add(entryHeaderId));
      
      const response = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entry-details?entryHeaderId=${entryHeaderId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.error) {
          console.error('[GeneralJournal] Backend error:', data.error);
          return;
        }
        
        // EntryDetails'leri düzleştirilmiş formata dönüştür
        const flattenedDetails = flattenEntryDetails(data.entryHeader);
        
        // Cache'e ekle
        setEntryDetailsCache(prev => new Map(prev).set(entryHeaderId, flattenedDetails));
      } else {
        const errorText = await response.text();
        console.error('[GeneralJournal] Error fetching details:', errorText);
      }
    } catch (err) {
      console.error('Error fetching entry details:', err);
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(entryHeaderId);
        return newSet;
      });
    }
  }, [entryDetailsCache, loadingDetails, flattenEntryDetails]);

  const handleDocumentChange = (event, newValue, reason) => {
    if (reason === 'selectOption') {
      setSelectedDocument(newValue);
      setPage(1);
      setExpandedEntries(new Set());
      setEntryDetailsCache(new Map());
      setError(null);
      setShouldExpandAll(false);
      // Keep search term - don't clear it when period changes
    } else if (reason === 'clear') {
      setSelectedDocument(null);
      setEntryHeaders([]);
      setEntryDetailsCache(new Map());
      setError(null);
      setShouldExpandAll(false);
      // Clear search only when document is cleared (not when changed)
      setAccountCodeSearch('');
      setDebouncedAccountCodeSearch('');
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0,00';
    const number = parseFloat(value);
    if (isNaN(number)) return '0,00';
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return '';
    const start = formatDate(doc.periodCoveredStart);
    const end = formatDate(doc.periodCoveredEnd);
    return `${start} - ${end} (${doc.uniqueId || doc.id || ''})`;
  };

  // Excel indir: tüm yevmiye (tüm sayfalar) + her entry'nin tüm detay satırları
  const handleDownloadExcel = useCallback(async () => {
    if (!selectedDocument) {
      toast.error('Please select a period first to download Excel.');
      return;
    }

    const progressToastId = toast.loading('Preparing Excel export...', { closeOnClick: false });
    const updateProgress = (message, opts = {}) => {
      toast.update(progressToastId, {
        render: message,
        isLoading: opts.isLoading ?? true,
        type: opts.type,
        autoClose: opts.autoClose,
        closeOnClick: opts.closeOnClick ?? false,
      });
    };

    const formatNum = (value) => {
      if (value === null || value === undefined) return '0,00';
      const number = parseFloat(value);
      if (isNaN(number)) return '0,00';
      return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
    };
    const formatDt = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const pageSize = 500;
    let allHeaders = [];

    try {
      updateProgress('Fetching journal entries...');
      const firstRes = await AuthService.authenticatedFetch(
        `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=0&size=${pageSize}`
      );
      if (!firstRes.ok) {
        updateProgress('Failed to fetch journal entries.', { isLoading: false, type: 'error', autoClose: 4000 });
        return;
      }
      const firstData = await firstRes.json();
      if (firstData.error) {
        updateProgress(firstData.error || 'Failed to fetch journal entries.', { isLoading: false, type: 'error', autoClose: 5000 });
        return;
      }
      const firstBatch = firstData.entryHeaders || [];
      allHeaders = [...firstBatch];
      const totalPages = firstData.totalPages || 1;
      const totalElements = firstData.totalElements || firstBatch.length;

      if (totalPages > 1) {
        // Avoid firing too many parallel requests on large datasets
        for (let p = 1; p < totalPages; p++) {
          updateProgress(`Fetching journal entries... (${p + 1}/${totalPages})`);
          const res = await AuthService.authenticatedFetch(
            `${EDEFTER_API_URL}/journal-entries-by-document-paginated?documentId=${selectedDocument.id}&page=${p}&size=${pageSize}`
          );
          if (!res.ok) continue;
          const data = await res.json();
          const batch = data?.entryHeaders || [];
          allHeaders = allHeaders.concat(batch);
        }
      }

      if (allHeaders.length === 0) {
        updateProgress('No journal entries found for this period.', { isLoading: false, type: 'warning', autoClose: 3500 });
        return;
      }
      updateProgress(`Found ${totalElements || allHeaders.length} entries. Preparing details...`);
    } catch (err) {
      console.error('Excel: yevmiye listesi alınırken hata', err);
      updateProgress('Failed to fetch journal entries. Please try again.', { isLoading: false, type: 'error', autoClose: 5000 });
      return;
    }

    let detailsMap = new Map(entryDetailsCache);
    const idsToFetch = allHeaders.filter((h) => !detailsMap.has(h.id)).map((h) => h.id);
    if (idsToFetch.length > 0) {
      updateProgress(`Fetching entry details... (0/${idsToFetch.length})`);

      const CHUNK_SIZE = 100; // keep querystring safe, avoid resource exhaustion
      try {
        for (let i = 0; i < idsToFetch.length; i += CHUNK_SIZE) {
          const chunk = idsToFetch.slice(i, i + CHUNK_SIZE);
          const idsString = chunk.join(',');
          const done = Math.min(i + chunk.length, idsToFetch.length);
          updateProgress(`Fetching entry details... (${done}/${idsToFetch.length})`);

          const response = await AuthService.authenticatedFetch(
            `${EDEFTER_API_URL}/journal-entry-details-batch?entryHeaderIds=${idsString}`
          );
          if (!response.ok) {
            throw new Error(`Batch details request failed (status=${response.status})`);
          }

          const data = await response.json();
          const batchResults = Array.isArray(data) ? data : (data?.results || data?.entryHeaders || []);

          batchResults.forEach((entryHeaderData) => {
            const headerId = entryHeaderData?.entryHeaderId ?? entryHeaderData?.entryHeader?.id;
            if (!headerId) return;
            const flattenedDetails = flattenEntryDetails(entryHeaderData);
            detailsMap.set(headerId, flattenedDetails);
          });
        }
      } catch (err) {
        console.error('Excel: detaylar yüklenirken hata', err);
        updateProgress('Failed to fetch entry details. Please try again.', { isLoading: false, type: 'error', autoClose: 5000 });
        return;
      }
    }

    updateProgress('Generating Excel file...');
    const headerRow = [
      'Entry #',
      'Entry Date',
      'Entry Comment',
      'Entry Debit',
      'Entry Credit',
      'Line #',
      'Posting Date',
      'Account Code',
      'Account Name',
      'Sub Account',
      'Debit',
      'Credit',
      'Comment',
    ];
    const rows = [headerRow];

    allHeaders.forEach((header) => {
      const details = detailsMap.get(header.id) || [];
      rows.push([
        header.entryNumber || '-',
        formatDt(header.enteredDate),
        header.entryComment || '',
        formatNum(header.totalDebit),
        formatNum(header.totalCredit),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
      details.forEach((d) => {
        const debitVal = d.debitCreditCode === 'D' ? formatNum(d.amount) : '';
        const creditVal = d.debitCreditCode === 'C' ? formatNum(d.amount) : '';
        const subAccount = d.accountSubId
          ? `${d.accountSubId}${d.accountSubDescription ? ' - ' + d.accountSubDescription : ''}`
          : '';
        rows.push([
          '',
          '',
          '',
          '',
          '',
          d.lineNumber ?? '',
          formatDt(d.postingDate),
          d.accountMainId ?? '',
          d.accountMainDescription ?? '',
          subAccount,
          debitVal,
          creditVal,
          d.detailComment ?? '',
        ]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'General Journal');
    let filename = 'general_journal';
    if (selectedDocument?.periodCoveredStart && selectedDocument?.periodCoveredEnd) {
      filename = `general_journal_${selectedIdentifier || 'export'}_${selectedDocument.periodCoveredStart}_${selectedDocument.periodCoveredEnd}`;
    }

    updateProgress('Downloading Excel...');
    XLSX.writeFile(wb, `${filename}.xlsx`);
    updateProgress(`Done. Exported ${allHeaders.length} journal entries.`, { isLoading: false, type: 'success', autoClose: 3000, closeOnClick: true });
  }, [
    selectedDocument,
    entryDetailsCache,
    selectedIdentifier,
    flattenEntryDetails,
  ]);

  // Check if account code starts with search term (strict matching)
  const matchesAccountCode = useCallback((accountMainId, accountSubId, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return true;
    const term = searchTerm.trim().toLowerCase();
    const mainId = (accountMainId || '').toLowerCase();
    const subId = (accountSubId || '').toLowerCase();
    // Use startsWith instead of includes - '12' matches '120', '121' but not '612'
    return mainId.startsWith(term) || (subId && subId.startsWith(term));
  }, []);

  // EntryHeaders zaten sayfalı geldiği için direkt kullanabiliriz
  // Strict filtering: Only show headers with matching details
  const paginatedGroups = useMemo(() => {
    const searchTerm = debouncedAccountCodeSearch;
    const hasSearch = searchTerm && searchTerm.trim();

    const groups = entryHeaders
      .map(header => {
        const allDetails = entryDetailsCache.get(header.id) || [];
        const isExpanded = expandedEntries.has(header.id);
        
        // Filter details based on search term (startsWith logic)
        const filteredDetails = hasSearch
          ? allDetails.filter(detail => 
              matchesAccountCode(detail.accountMainId, detail.accountSubId, searchTerm)
            )
          : allDetails;
        
        // STRICT FILTERING: If search exists, only show headers with matching details
        // If details are not loaded yet, show header temporarily (will be filtered once details load)
        if (hasSearch) {
          if (allDetails.length === 0) {
            // Details not loaded yet - show header temporarily with loading indicator
            // Details will be loaded in background and header will be filtered automatically once loaded
          } else {
            // Details are loaded - strict check: must have at least one matching detail
            if (filteredDetails.length === 0) {
              return null; // Exclude this header - no matching details
            }
          }
        }
        
        // Detayların toplamlarını filtrelemiş detaylardan hesapla
        const detailTotalDebit = filteredDetails.reduce((sum, detail) => {
          return sum + (detail.debitCreditCode === 'D' ? parseFloat(detail.amount || 0) : 0);
        }, 0);
        const detailTotalCredit = filteredDetails.reduce((sum, detail) => {
          return sum + (detail.debitCreditCode === 'C' ? parseFloat(detail.amount || 0) : 0);
        }, 0);
        
        return {
          headerId: header.id,
          entryNumber: header.entryNumber,
          entryComment: header.entryComment,
          enteredBy: header.enteredBy,
          enteredDate: header.enteredDate,
          totalDebit: header.totalDebit,
          totalCredit: header.totalCredit,
          details: filteredDetails, // Filtered details (only matching ones)
          detailTotalDebit: detailTotalDebit,
          detailTotalCredit: detailTotalCredit,
          isExpanded: isExpanded,
          isLoadingDetails: allDetails.length === 0 && hasSearch, // Show loading indicator if details not loaded and search is active
        };
      })
      .filter(group => group !== null); // Remove filtered out headers
    
    return groups;
  }, [entryHeaders, entryDetailsCache, expandedEntries, debouncedAccountCodeSearch, matchesAccountCode]);

  const handleToggleEntry = (headerId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(headerId)) {
      newExpanded.delete(headerId);
    } else {
      newExpanded.add(headerId);
      // Detayları yükle (eğer cache'de yoksa)
      if (!entryDetailsCache.has(headerId)) {
        fetchEntryDetails(headerId);
      }
    }
    setExpandedEntries(newExpanded);
  };

  const handleExpandAll = useCallback(() => {
    if (!selectedDocument) return;
    
    // Tüm entry header'ları için details yükle ve expanded yap
    const allHeaderIds = new Set(entryHeaders.map(h => h.id));
    allHeaderIds.forEach(headerId => {
      if (!entryDetailsCache.has(headerId)) {
        fetchEntryDetails(headerId);
      }
    });
    setExpandedEntries(allHeaderIds);
    setShouldExpandAll(true);
  }, [selectedDocument, entryHeaders, entryDetailsCache, fetchEntryDetails]);

  const handleCollapseAll = useCallback(() => {
    setExpandedEntries(new Set());
    setShouldExpandAll(false);
  }, []);

  const allExpanded = entryHeaders.length > 0 && expandedEntries.size === entryHeaders.length && shouldExpandAll;

  const handleExpandCollapseToggle = (event) => {
    if (event.target.checked) {
      handleExpandAll();
    } else {
      handleCollapseAll();
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
            p: 3,
            color: 'white'
          }}>
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <AccountBalance sx={{ fontSize: '1.5rem' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  General Journal
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  View detailed journal entries (yevmiye kayıtları) by company and period
                </Typography>
              </Grid>
              <Grid item sx={{ ml: 'auto' }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={() => navigate('/edefter/upload')}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.5)',
                    },
                    fontWeight: 600,
                    textTransform: 'none',
                  }}
                >
                  Upload General Journal
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            <Box>
              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

            {/* Document Selection */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {!selectedIdentifier && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Please select a company from the header to view journal entries.
                    </Alert>
                  </Grid>
                )}

                <Grid item xs={12} sm={12} md={6} lg={6}>
                  <Autocomplete
                    options={documents}
                    value={selectedDocument}
                    onChange={handleDocumentChange}
                    disabled={!selectedIdentifier || loadingDocuments}
                    loading={loadingDocuments}
                    getOptionLabel={(option) => getDocumentLabel(option)}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;
                      if (option.id && value.id) return option.id === value.id;
                      return option.uniqueId === value.uniqueId && 
                             option.periodCoveredStart === value.periodCoveredStart &&
                             option.periodCoveredEnd === value.periodCoveredEnd;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="General Journal Period"
                        variant="outlined"
                        fullWidth
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarToday color={selectedDocument ? 'primary' : 'disabled'} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <>
                              {loadingDocuments ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {getDocumentLabel(option)}
                      </Box>
                    )}
                    sx={{
                      width: '100%',
                      display: 'block',
                      '& .MuiAutocomplete-root': {
                        width: '100%'
                      },
                      '& .MuiAutocomplete-inputRoot': {
                        width: '100% !important',
                        minWidth: '100% !important',
                        flexWrap: 'nowrap'
                      },
                      '& .MuiAutocomplete-input': {
                        width: '100% !important',
                        minWidth: 0,
                        flex: '1 1 auto'
                      }
                    }}
                  />
                </Grid>

                {/* Account Code Search */}
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <TextField
                    label="Account Code Search"
                    variant="outlined"
                    fullWidth
                    value={accountCodeSearch}
                    onChange={(e) => setAccountCodeSearch(e.target.value)}
                    disabled={!selectedDocument}
                    placeholder="e.g. 12, 120..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color={selectedDocument && accountCodeSearch ? 'primary' : 'disabled'} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {loading && debouncedAccountCodeSearch ? (
                            <CircularProgress size={20} />
                          ) : accountCodeSearch ? (
                            <IconButton
                              size="small"
                              onClick={() => setAccountCodeSearch('')}
                              edge="end"
                              sx={{ 
                                padding: '4px',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          ) : null}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Expand/Collapse All Switch */}
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '100%',
                    pt: 1
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Switch
                        checked={allExpanded}
                        onChange={handleExpandCollapseToggle}
                        disabled={!selectedDocument || entryHeaders.length === 0}
                        size="medium"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'primary.main',
                          },
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          color: allExpanded ? 'primary.main' : 'text.secondary',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Journal Entries Table */}
            {loading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Loading journal entries...
                </Typography>
              </Box>
            )}

            {!loading && entryHeaders.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Description sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Journal Entries ({totalElements} total entries, page {page} of {totalPages})
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Download />}
                    onClick={handleDownloadExcel}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, rgba(16, 38, 166) 0%, rgba(14, 33, 148) 50%, rgba(12, 28, 130) 100%)',
                      boxShadow: '0 4px 15px rgba(16, 38, 166, 0.4)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(20, 45, 180, 1) 0%, rgba(18, 40, 170, 1) 50%, rgba(16, 38, 166) 100%)',
                        boxShadow: '0 6px 20px rgba(16, 38, 166, 0.6)',
                      }
                    }}
                  >
                    Download Excel
                  </Button>
                </Box>

                <Card elevation={8} sx={{ borderRadius: 3 }}>
                  <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap', width: 50 }}>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Entry #
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Comment
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Debit
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', textAlign: 'right', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            Credit
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedGroups.map((group) => {
                          const isExpanded = expandedEntries.has(group.headerId);
                          const isLoadingDetails = loadingDetails.has(group.headerId);
                          const hasDetails = group.details.length > 0;
                          const shouldShowDetails = isExpanded && (hasDetails || isLoadingDetails);

                          return (
                            <React.Fragment key={`entry-${group.headerId}`}>
                              {/* Entry Header Row */}
                              <TableRow 
                                hover
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                  },
                                  borderLeft: '4px solid',
                                  borderColor: 'primary.main',
                                  backgroundColor: 'rgba(25, 118, 210, 0.02)'
                                }}
                              >
                                <TableCell sx={{ width: 50 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleEntry(group.headerId)}
                                    sx={{ p: 0.5 }}
                                  >
                                    {isExpanded ? (
                                      <ExpandMore sx={{ fontSize: 20 }} />
                                    ) : (
                                      <ChevronRight sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.85rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {group.entryNumber || '-'}
                                    {group.isLoadingDetails && (
                                      <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.85rem' }}>
                                  {formatDate(group.enteredDate)}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                  {group.entryComment || '-'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                                  {formatNumber(group.totalDebit)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>
                                  {formatNumber(group.totalCredit)}
                                </TableCell>
                              </TableRow>
                              
                              {/* Entry Details (Collapsed) */}
                              {shouldShowDetails && (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ py: 0, border: 'none' }}>
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ margin: 0 }}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
                                              <TableCell sx={{ width: 50 }} />
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Line #
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Posting Date
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Code
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Account Name
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Sub Account
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Debit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                                                Credit
                                              </TableCell>
                                              <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                                                Comment
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {isLoadingDetails ? (
                                              <TableRow>
                                                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 2 }}>
                                                  <CircularProgress size={20} />
                                                  <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                                                    Loading details...
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            ) : hasDetails ? (
                                              group.details.map((detail, idx) => (
                                                <TableRow 
                                                  key={`detail-${detail.entryDetailId}-${idx}`}
                                                  sx={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                    '&:hover': {
                                                      backgroundColor: 'rgba(25, 118, 210, 0.06)',
                                                    },
                                                  }}
                                                >
                                                  <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                                    {detail.lineNumber || '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                                    {formatDate(detail.postingDate)}
                                                  </TableCell>
                                                  <TableCell 
                                                    sx={{ 
                                                      fontWeight: 600, 
                                                      color: 'primary.main', 
                                                      fontSize: '0.8rem', 
                                                      fontFamily: 'monospace',
                                                    }}
                                                  >
                                                    {detail.accountMainId || '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                                    {detail.accountMainDescription || '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                    {detail.accountSubId ? `${detail.accountSubId} - ${detail.accountSubDescription || ''}` : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                    {detail.debitCreditCode === 'D' ? formatNumber(detail.amount) : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ textAlign: 'right', fontWeight: 600, color: 'text.primary', fontSize: '0.8rem' }}>
                                                    {detail.debitCreditCode === 'C' ? formatNumber(detail.amount) : '-'}
                                                  </TableCell>
                                                  <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                    {detail.detailComment || '-'}
                                                  </TableCell>
                                                </TableRow>
                                              ))
                                            ) : (
                                              <TableRow>
                                                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 2 }}>
                                                  <Typography variant="body2" color="text.secondary">
                                                    {debouncedAccountCodeSearch && debouncedAccountCodeSearch.trim()
                                                      ? `No details found matching account code "${debouncedAccountCodeSearch}"`
                                                      : 'No details found'}
                                                  </Typography>
                                                </TableCell>
                                              </TableRow>
                                            )}
                                            {/* Total Row */}
                                            {hasDetails && group.details.length > 0 && (() => {
                                              const totalsEqual = Math.abs(group.detailTotalDebit - group.detailTotalCredit) < 0.01; // Float karşılaştırması için küçük tolerans
                                              const totalColor = totalsEqual ? 'success.main' : 'error.main';
                                              return (
                                                <TableRow sx={{ 
                                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                  borderTop: '2px solid',
                                                  borderColor: 'primary.main',
                                                  '& td': {
                                                    fontWeight: 700,
                                                    fontSize: '0.85rem',
                                                  }
                                                }}>
                                                  <TableCell sx={{ width: 50, pl: 4 }}></TableCell>
                                                  <TableCell colSpan={5} sx={{ fontWeight: 700, fontSize: '0.85rem', textAlign: 'right' }}>
                                                    Total:
                                                  </TableCell>
                                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: totalColor, fontSize: '0.85rem' }}>
                                                    {formatNumber(group.detailTotalDebit)}
                                                  </TableCell>
                                                  <TableCell sx={{ textAlign: 'right', fontWeight: 700, color: totalColor, fontSize: '0.85rem' }}>
                                                    {formatNumber(group.detailTotalCredit)}
                                                  </TableCell>
                                                  <TableCell></TableCell>
                                                </TableRow>
                                              );
                                            })()}
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(event, value) => {
                          setPage(value);
                          // Only reset expanded entries if expand all is not active
                          if (!shouldExpandAll) {
                            setExpandedEntries(new Set());
                          }
                          // Note: If shouldExpandAll is true, the useEffect will handle expanding entries
                        }}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </Card>
              </Box>
            )}

              {/* Empty State */}
              {!loading && !selectedDocument && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Box sx={{ display: 'inline-flex', p: 3, borderRadius: 3, backgroundColor: 'grey.100', mb: 2 }}>
                    <AccountBalance sx={{ fontSize: 48, color: 'grey.400' }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, color: 'text.primary', fontSize: '1rem' }}>
                    No Journal Selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    Select a company from the header and period to view journal entries.
                  </Typography>
                </Box>
              )}

              {!loading && selectedDocument && entryHeaders.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    No journal entries found for the selected period.
                  </Typography>
                </Box>
              )}
            </Box>

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default GeneralJournal;

